<template>
  <div class="apartments">
    
    <!-- Icons section -->
    
    <div class="apartments-grid">
      <div v-for="n in 2" :key="n" class="apartment-card">
        <img :src="getApartmentImage(n)" :alt="`Apartment ${apartment_names[n-1]}`">
        <div class="apartment-details">
          <h2>{{ $t(`apartments.ap${n}.title`) }} {{ apartment_names[n-1] }}</h2>
          <p>{{ $t(`apartments.ap${n}.description`) }}</p>
          <ol class="apartment-details-list">
            <li>{{ $t(`apartments.ap${n}.bedrooms`) }}</li>
            <li>{{ $t(`apartments.ap${n}.bathrooms`) }}</li>
            <li>{{ $t(`apartments.ap${n}.kitchen`) }}</li>
            <li>{{ $t(`apartments.ap${n}.wifi`) }}</li>
          </ol>
          <p>{{ $t(`apartments.ap${n}.price`) }}</p>
          <div class="apartment-actions">
            <button class="btn-primary" @click="toggleCalendar(n)">
              {{ $t('apartments.calendar') }}
            </button>
            <button class="btn-primary" @click="toggleGallery(n)">
              {{ $t('apartments.gallery') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Calendar section outside the grid -->
    <CalendarPicker 
      v-if="selectedApartment"
      :apartmentId="selectedApartment" 
      @close="closeCalendar"
    />

    <!-- Gallery section (to be implemented) -->
    <div v-if="showGallery" class="gallery-section">
      <!-- Gallery implementation will go here -->
    </div>
    <div class="icons-section">
      <div v-for="icon in icons" :key="icon.name" class="icon-item">
        <img :src="icon.src" :alt="$t(`apartments.${icon.name}`)" class="icon-image">
        <h2 class="icon-title">{{ $t(`apartments.${icon.name}`) }}</h2>
      </div>
    </div>
  </div>
</template>

<script>
import CalendarPicker from './components/calendar_picker.vue';
import { logMessage } from '@/utils/flaskapi';
const iconContext = require.context('@/assets/icons', false, /\.(png|jpe?g|svg|gif)$/);


export default {
  name: 'Apartments',
  components: {
    CalendarPicker
  },
  data() {
    return {
      icons: [],
      apartment_names: ['Gerlach', 'Vysoká'],
      selectedApartment: null,
      showGallery: false,
      galleryApartment: null,
      routeMappings: {
        'en': '/gallery',
        'sk': '/galeria',
        'de': '/galerie',
        'pl': '/galeria',
        'hu': '/galeria'
      }
    }
  },
  async created() {
    this.loadIcons();
  },
  methods: {
    loadIcons() {
      const iconFiles = iconContext.keys();
      this.icons = iconFiles.map(filePath => {
        // Extract filename and remove extension
        const fileName = filePath.replace(/^\.\//, '').replace(/\.(png|jpe?g|svg|gif)$/, '');
        
        // Extract the name part after the number (e.g., "01_parking" -> "parking")
        const nameWithoutNumber = fileName.replace(/^\d+_/, '');
        
        return {
          name: nameWithoutNumber,
          src: iconContext(filePath),
          originalName: fileName
        };
      }).sort((a, b) => {
        // Sort by the number prefix (e.g., "01_parking" comes before "02_mountains")
        const aNumber = parseInt(a.originalName.match(/^(\d+)_/)?.[1] || '0');
        const bNumber = parseInt(b.originalName.match(/^(\d+)_/)?.[1] || '0');
        return aNumber - bNumber;
      });
    },
    toggleCalendar(apartmentId) {
      logMessage('info', 'toggleCalendarClickedForApartment_' + apartmentId);
      this.selectedApartment = this.selectedApartment === apartmentId ? null : apartmentId;
      if (this.selectedApartment) {
        this.showGallery = false;
      }
    },
    closeCalendar() {
      this.selectedApartment = null;
    },
    toggleGallery(apartmentId) {
      logMessage('info', 'toggleGalleryClickedForApartment_' + apartmentId);
      const locale = this.$route.path.split('/')[1];
      const galleryLocale = this.routeMappings[locale];
      this.$router.push(`/${locale}${galleryLocale}/ap${apartmentId}`);

    },
    getApartmentImage(n) {
      return require(`@/assets/images/apartments_hero/ap${n}.jpg`);
    }
  }
}
</script>

<style scoped>
.apartments {
  padding: 1rem;
  padding-top: var(--header-height, 2em);
  padding-bottom: 8em;
  max-width: 1100px;
  margin-left: auto;
  margin-right: auto;
}

.apartment-details-list {
  list-style-type: disc;
  padding-left: 1rem;
  margin-left: 1rem;
  margin: 1rem 0;
  font-size: 0.9rem;
  color: #666;
}
.apartment-details-list li {
  margin-left: 0.5rem;
}

.apartments h1 {
  text-align: center;
  margin-bottom: 2rem;
}

/* Icons section styles */
.icons-section {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-evenly; /* Changed from space-between to space-evenly */
  align-items: flex-start;
  width: 100%;
  max-width: 1000px;
  gap: 1rem;
  margin: 2rem 0;
  padding: 2rem;
  border-radius: 12px;
  overflow-x: auto;
}

.icon-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 0.5rem;
  transition: transform 0.2s ease;
  flex: 1; /* Allow items to grow and fill available space */
  min-width: 5rem; /* Minimum width instead of fixed width */
  max-width: 8rem; /* Maximum width to prevent items from getting too wide */
  min-height: 6rem; /* Ensure consistent height */
  justify-content: flex-start; /* Align content to top */
}

.icon-item:hover {
  transform: translateY(-5px);
}

.icon-image {
  width: 3rem;
  height: 3rem;
  object-fit: contain;
  filter: drop-shadow(5px 5px 2px rgba(141, 177, 214, 0.51));
}

.icon-title {
  font-size: 1rem;
  font-weight: 600;
  color: var(--primary-color);
  margin: 0;
  margin-top: 0.5rem; /* Consistent spacing from icon */
  text-transform: capitalize;
  line-height: 1.2; /* Better line height for text wrapping */
  height: 3rem; /* Fixed height for all text areas */
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  width: 100%;
}

.apartments-grid {
  display: flex;
  flex-wrap: wrap;
  max-width: 1000px;
  gap: 2rem;
  margin-top: 2rem;
  justify-content: center; /* Center cards horizontally */
  align-items: flex-start;
  margin-left: auto;
  margin-right: auto; /* Center the grid itself if only one card */
}

.apartment-card {
  background: var(--tertiary-color);
  border-radius: 8px;
  max-width: 470px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin: 0; /* Let flexbox handle spacing */
  transition: transform 0.3s ease;
}

.apartment-card:hover {
  transform: translateY(-5px);
}

.apartment-card img {
  width: 100%;
  height: 200px;
  object-fit: cover;
}

.apartment-details {
  padding: 1.5rem;
}

.apartment-details h2 {
  margin-bottom: 1rem;
  color: #333;
}

.apartment-details p {
  color: #666;
  margin-bottom: 1rem;
}

.apartment-details ul {
  list-style: none;
  padding: 0;
  margin: 1rem 0;
}

.apartment-details li {
  margin: 0.5rem 0;
}

.apartment-actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-top: 1rem;
}

.btn-primary {
  background: var(--primary-color);
  color: white;
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  width: 100%;
}

.btn-primary:hover {
  background: var(--primary-color-light-accent);
}

.calendar-section {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: white;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  width: 90%;
  max-width: 400px;
  max-height: 50vh;
  overflow-y: auto;
  z-index: 1000;
}

.calendar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.btn-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  color: #666;
}

.btn-close:hover {
  color: #333;
}

@media (max-width: 768px) {
  .apartments-container {
    padding: 1rem;
  }
  
  .apartments-grid {
    grid-template-columns: 1fr;
  }

  .calendar-section {
    padding: 1rem;
    width: 100%;
    height: 100%;
    max-height: none;
    border-radius: 0;
  }
}
</style>
